import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/apiClient';
import RecipeEditor from '../../components/Canvas/RecipeEditor';
import { IngredientLinesProvider } from '../../contexts/IngredientLinesContext';
import {
  BULK_FORMATS,
  blankCardBlob,
  blankCardFileName,
  zipBlankCards,
} from '../../utils/bulkBlankExport';

const READY_TIMEOUT_MS = 20000;
const SETTLE_MS = 300;
const CAPTURE_ATTEMPTS = 4;

const REMOUNT_ATTEMPTS = 3;

class BackgroundMissingError extends Error {
  constructor() {
    super('Template background did not load.');
  }
}

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

async function waitForCard(cardRef) {
  const start = Date.now();
  while (!cardRef.current?.isConnected) {
    if (Date.now() - start > READY_TIMEOUT_MS) throw new Error('Card did not finish loading.');
    await sleep(100);
  }
}

// The editor can briefly swap the card for its loading skeleton while it settles, which
// detaches the node. Wait for it to stay put, capture, and retry if it was replaced.
async function captureSettledCard(cardRef, format) {
  let lastError = new Error('Card did not finish loading.');
  for (let attempt = 0; attempt < CAPTURE_ATTEMPTS; attempt += 1) {
    try {
      await waitForCard(cardRef);
      const node = cardRef.current;
      await nextFrame();
      await sleep(SETTLE_MS);
      if (cardRef.current !== node || !node.isConnected) continue;
      // A failed template request (e.g. rate limited) leaves the card plain white. Never
      // export that as if it were the design.
      if (getComputedStyle(node).backgroundImage === 'none') {
        throw new BackgroundMissingError();
      }
      const blob = await blankCardBlob(node, format);
      if (cardRef.current === node && node.isConnected) return blob;
    } catch (err) {
      if (err instanceof BackgroundMissingError) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export default function BlankCardsPage() {
  const cardRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [format, setFormat] = useState('pdf');
  const [current, setCurrent] = useState(null); // template code currently rendered offscreen
  const [progress, setProgress] = useState(null); // { done, total, name }
  const [failures, setFailures] = useState([]);
  const [loadError, setLoadError] = useState('');
  const running = progress !== null;

  useEffect(() => {
    let ignore = false;
    api.get('/api/templates/')
      .then(res => {
        if (ignore) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setTemplates(list);
        setSelected(new Set(list.map(t => t.code)));
      })
      .catch(() => { if (!ignore) setLoadError('Could not load the template list.'); });
    return () => { ignore = true; };
  }, []);

  const toggle = code => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const generate = async () => {
    const chosen = templates.filter(t => selected.has(t.code));
    if (!chosen.length) return;
    const entries = [];
    const failed = [];
    setFailures([]);

    for (let i = 0; i < chosen.length; i += 1) {
      const template = chosen[i];
      setProgress({ done: i, total: chosen.length, name: template.title });
      try {
        let blob = null;
        for (let attempt = 0; attempt < REMOUNT_ATTEMPTS && !blob; attempt += 1) {
          // Remount the card for every attempt so the editor re-requests the template.
          setCurrent(null);
          await nextFrame();
          setCurrent(template.code);
          try {
            blob = await captureSettledCard(cardRef, format);
          } catch (err) {
            if (!(err instanceof BackgroundMissingError) || attempt === REMOUNT_ATTEMPTS - 1) throw err;
            await sleep(1500 * (attempt + 1));
          }
        }
        entries.push({ name: blankCardFileName(template.title, template.code, format), blob });
      } catch (err) {
        console.error('Blank card export failed', template.code, err);
        failed.push(template.title || template.code);
      }
    }

    setCurrent(null);
    setProgress(null);
    setFailures(failed);

    if (!entries.length) {
      toast.error('No cards were generated.');
      return;
    }
    const zip = await zipBlankCards(entries);
    downloadBlob(zip, `blank-recipe-cards-${format}.zip`);
    toast.success(`Generated ${entries.length} blank card${entries.length === 1 ? '' : 's'}.`);
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-semibold text-charcoal mb-1">Blank card export</h1>
      <p className="text-sm text-slate-600 mb-4">
        Admin only. Renders each template with the current editor and exports the same blank card as the
        normal Share export, bundled into one ZIP.
      </p>

      {loadError && <p className="text-red-600 mb-3">{loadError}</p>}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm">
          Format{' '}
          <select
            value={format}
            onChange={e => setFormat(e.target.value)}
            disabled={running}
            className="border rounded px-2 py-1"
          >
            {Object.entries(BULK_FORMATS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="text-sm underline"
          disabled={running}
          onClick={() => setSelected(new Set(templates.map(t => t.code)))}
        >
          Select all
        </button>
        <button
          type="button"
          className="text-sm underline"
          disabled={running}
          onClick={() => setSelected(new Set())}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={generate}
          disabled={running || selected.size === 0}
          className="ml-auto px-4 py-2 rounded bg-dark-purple text-white disabled:opacity-50"
        >
          {running ? 'Generating…' : `Generate ${selected.size} card${selected.size === 1 ? '' : 's'}`}
        </button>
      </div>

      {running && (
        <p className="text-sm mb-3" role="status">
          {progress.done + 1} of {progress.total}: {progress.name}
        </p>
      )}
      {failures.length > 0 && (
        <p className="text-sm text-red-600 mb-3">
          Failed: {failures.join(', ')}. These were left out of the ZIP.
        </p>
      )}

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {templates.map(t => (
          <li key={t.code}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.has(t.code)}
                disabled={running}
                onChange={() => toggle(t.code)}
              />
              {t.title}
            </label>
          </li>
        ))}
      </ul>

      {/* Offscreen render target, same pattern as the Share button's blank export. */}
      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
        <IngredientLinesProvider recipeId="admin-blank-cards">
          {current && (
            <div style={{ width: '600px', margin: '0 auto', padding: '0', fontSize: '16px' }}>
              <RecipeEditor
                key={current}
                recipeCardRef={cardRef}
                templateCode={current}
                forceBlank
                hidePlaceholders
              />
            </div>
          )}
        </IngredientLinesProvider>
      </div>
    </div>
  );
}
